/**
 * Personal Inventory
 * Doc: docs/overall-plan (Inventory & Assets - Personal Inventory)
 * Full CRUD: list from GET /items, add via POST /items, delete via DELETE /items/:id.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { personalInventoryApi, type PersonalInventoryItem } from '../services/api';
import { showToast, askConfirm } from 'shared-ui';

const PersonalInventory: React.FC = () => {
  const [items, setItems] = useState<PersonalInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newWarranty, setNewWarranty] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editWarranty, setEditWarranty] = useState('');
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const res = await personalInventoryApi.getItems();
      if (res?.success && Array.isArray(res?.data)) setItems(res.data);
      else setError(res?.error || 'Failed to load inventory');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setAdding(true);
    const res = await personalInventoryApi.createItem({
      name,
      category: newCategory.trim() || undefined,
      notes: newNotes.trim() || undefined,
      location: newLocation.trim() || undefined,
      warrantyExpiresAt: newWarranty.trim() || undefined,
    });
    setAdding(false);
    if (res?.success && res?.data) {
      setItems((prev) => [res.data, ...prev]);
      setNewName('');
      setNewCategory('');
      setNewNotes('');
      setNewLocation('');
      setNewWarranty('');
    } else showToast(res?.error || 'Failed to add item', 'error');
  };

  const startEdit = (item: PersonalInventoryItem) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditCategory(item.category ?? '');
    setEditNotes(item.notes ?? '');
    setEditLocation(item.location ?? '');
    setEditWarranty(item.warrantyExpiresAt ? item.warrantyExpiresAt.slice(0, 10) : '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditCategory('');
    setEditNotes('');
    setEditLocation('');
    setEditWarranty('');
  };

  const handleUpdate = async (id: string) => {
    const name = editName.trim();
    if (!name) return;
    setSaving(true);
    const res = await personalInventoryApi.updateItem(id, {
      name,
      category: editCategory.trim() || undefined,
      notes: editNotes.trim() || undefined,
      location: editLocation.trim() || undefined,
      warrantyExpiresAt: editWarranty.trim() || undefined,
    });
    setSaving(false);
    if (res?.success && res?.data) {
      setItems((prev) => prev.map((i) => (i.id === id ? res.data : i)));
      cancelEdit();
    } else showToast(res?.error || 'Failed to update', 'error');
  };

  const handleDelete = async (id: string) => {
    if (!(await askConfirm({ title: 'Remove this item from your inventory?', confirmLabel: 'Remove', danger: true }))) return;
    const res = await personalInventoryApi.deleteItem(id);
    if (res?.success) {
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (editingId === id) cancelEdit();
    } else showToast(res?.error || 'Failed to delete', 'error');
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
        <div style={{ color: '#c00', marginBottom: '16px' }}>{error}</div>
        <p style={{ color: '#666' }}>Personal inventory will be available when you’re signed in with a personal account.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '560px', margin: '0 auto', padding: '24px' }}>
      <div style={{
        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        color: 'white',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: '0 0 8px 0', fontSize: '24px' }}>Personal Inventory</h1>
            <p style={{ margin: 0, opacity: 0.95, fontSize: '14px' }}>
              Track your personal items, assets, and collections.
            </p>
          </div>
          <button type="button" onClick={() => load(true)} disabled={refreshing} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', borderRadius: '8px', color: 'white', cursor: refreshing ? 'not-allowed' : 'pointer', fontSize: '14px' }}>
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <form onSubmit={handleAdd} style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <input
            type="text"
            placeholder="Item name *"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          />
          <input
            type="text"
            placeholder="Category (optional)"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          />
          <input
            type="text"
            placeholder="Notes (optional)"
            value={newNotes}
            onChange={(e) => setNewNotes(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          />
          <input
            type="text"
            placeholder="Location (optional)"
            value={newLocation}
            onChange={(e) => setNewLocation(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          />
          <input
            type="date"
            placeholder="Warranty expires (optional)"
            value={newWarranty}
            onChange={(e) => setNewWarranty(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}
          />
          <button
            type="submit"
            disabled={adding || !newName.trim()}
            style={{
              padding: '10px 16px',
              background: '#11998e',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: adding ? 'wait' : 'pointer',
            }}
          >
            {adding ? 'Adding…' : 'Add item'}
          </button>
        </div>
      </form>

      {items.length === 0 ? (
        <div style={{
          background: '#f8fafc',
          borderRadius: '12px',
          padding: '32px',
          textAlign: 'center',
          color: '#64748b',
          fontSize: '14px',
        }}>
          No items yet. Add an item above to get started.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {items.map((item) => (
            <li
              key={item.id}
              style={{
                padding: '12px 16px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              {editingId === item.id ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    placeholder="Item name *"
                    style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <input
                    type="text"
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value)}
                    placeholder="Category"
                    style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <input
                    type="text"
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    placeholder="Notes"
                    style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <input
                    type="text"
                    value={editLocation}
                    onChange={(e) => setEditLocation(e.target.value)}
                    placeholder="Location"
                    style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <input
                    type="date"
                    value={editWarranty}
                    onChange={(e) => setEditWarranty(e.target.value)}
                    style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  />
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleUpdate(item.id)}
                      disabled={saving || !editName.trim()}
                      style={{ padding: '6px 12px', fontSize: '12px', background: '#11998e', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'wait' : 'pointer' }}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" onClick={cancelEdit} style={{ padding: '6px 12px', fontSize: '12px', background: '#e2e8f0', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{item.name}</span>
                    {item.category && (
                      <span style={{ marginLeft: '8px', fontSize: '13px', color: '#64748b' }}>({item.category})</span>
                    )}
                    {item.location && (
                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>📍 {item.location}</div>
                    )}
                    {item.warrantyExpiresAt && (
                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '2px' }}>
                        Warranty to {new Date(item.warrantyExpiresAt).toLocaleDateString()}
                      </div>
                    )}
                    {item.notes && (
                      <div style={{ fontSize: '13px', color: '#64748b', marginTop: '4px' }}>{item.notes}</div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => startEdit(item)}
                      style={{ padding: '6px 12px', fontSize: '12px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(item.id)}
                      style={{ padding: '6px 12px', fontSize: '12px', background: '#fef2f2', color: '#b91c1c', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                    >
                      Remove
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PersonalInventory;
